/* Lightweight locale context for the Prayer UI (en/ur/ar) + RTL direction.
 *
 * Mirrors the landing widget's window.AP_I18N contract (current locale + an
 * ap:languagechange signal) but as native React state. The arc/windows UI
 * strings below are ported VERBATIM from prayer-hero.js (phApplyLocaleText /
 * phLocaleCopy). Locale is persisted to the raw key `ap_prayer_locale_v1` so it
 * can be shared with the landing origin.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type Locale = 'en' | 'ur' | 'ar';
export const LOCALES: Locale[] = ['en', 'ur', 'ar'];
const LOCALE_KEY = 'ap_prayer_locale_v1';

export function isRTL(locale: Locale): boolean {
  return locale === 'ar' || locale === 'ur';
}

export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'EN',
  ur: 'اردو',
  ar: 'العربية'
};

/* Prayer-card UI strings (VERBATIM from phApplyLocaleText + phLocaleCopy). */
export interface ArcCopy {
  pill: string;
  nextPrayer: string;
  countdown: string;
  units: [string, string, string];
  since: string;
  interval: string;
  arc: string;
  strict: string;
  makruh: string;
  makruhMaliki: string;
  ishraq: string;
  at: string;
  ends: string;
  m: string;
  makruhShort: string;
  windowsToggle: string;
  schoolOfLaw: string;
  currentLabel: string;
  nextLabel: string;
  disclaimer: string;
}

export const ARC_COPY: Record<Locale, ArcCopy> = {
  en: {
    pill: 'Today · Live',
    nextPrayer: 'Next prayer',
    countdown: 'Time remaining',
    units: ['Hours', 'Minutes', 'Seconds'],
    since: 'Since',
    interval: 'of interval elapsed',
    arc: "The day's arc",
    strict: 'Strictly forbidden',
    makruh: 'Not recommended for nawafil',
    makruhMaliki: 'Disliked (makruh)',
    ishraq: 'Ishraq / permissible',
    at: 'at',
    ends: 'Ends in',
    m: 'm',
    makruhShort: 'Makruh',
    windowsToggle: 'Prayer Windows',
    schoolOfLaw: 'School of law',
    currentLabel: 'Current',
    nextLabel: 'Next',
    disclaimer:
      'These makruh/forbidden windows are derived from classical fiqh positions across the four Sunni madhabs and depend on local sunrise/sunset and your mosque’s jamaat timing. They are guidance, not a fatwa — follow your local scholar where practice differs.'
  },
  ar: {
    pill: 'اليوم · مباشر',
    nextPrayer: 'الصلاة التالية',
    countdown: 'الوقت المتبقي',
    units: ['ساعات', 'دقائق', 'ثوانٍ'],
    since: 'منذ',
    interval: 'من الفترة المنقضية',
    arc: 'مسار اليوم',
    strict: 'ممنوع تماماً',
    makruh: 'مكروه للنوافل',
    makruhMaliki: 'مكروه',
    ishraq: 'الإشراق / مباح',
    at: 'عند',
    ends: 'ينتهي خلال',
    m: 'د',
    makruhShort: 'مكروه',
    windowsToggle: 'أوقات الكراهة والحظر',
    schoolOfLaw: 'المذهب الفقهي',
    currentLabel: 'الحالية',
    nextLabel: 'التالية',
    disclaimer:
      'هذه أوقات الكراهة والحظر مستنبطة من أقوال الفقه الكلاسيكي في المذاهب السنية الأربعة، وتعتمد على شروق وغروب الشمس محلياً وعلى توقيت جماعة مسجدك. وهي للإرشاد لا فتوى — اتبع عالمك المحلي حيث يختلف العمل.'
  },
  ur: {
    pill: 'آج · لائیو',
    nextPrayer: 'اگلی نماز',
    countdown: 'باقی وقت',
    units: ['گھنٹے', 'منٹ', 'سیکنڈ'],
    since: 'سے',
    interval: 'وقفہ گزر چکا',
    arc: 'دن کا مدار',
    strict: 'سخت ممانعت',
    makruh: 'نوافل کے لیے مناسب نہیں',
    makruhMaliki: 'مکروہ',
    ishraq: 'اشراق / جائز',
    at: 'بوقت',
    ends: 'اختتام',
    m: 'منٹ',
    makruhShort: 'مکروہ',
    windowsToggle: 'اوقاتِ کراہت و ممانعت',
    schoolOfLaw: 'فقہی مذہب',
    currentLabel: 'موجودہ',
    nextLabel: 'اگلی',
    disclaimer:
      'یہ مکروہ و ممنوع اوقات چاروں سنی مذاہب کے کلاسیکی فقہی اقوال سے ماخوذ ہیں اور مقامی طلوع و غروبِ آفتاب اور آپ کی مسجد کی جماعت کے وقت پر منحصر ہیں۔ یہ رہنمائی ہے، فتویٰ نہیں — جہاں عمل مختلف ہو وہاں اپنے مقامی عالم کی پیروی کریں۔'
  }
};

/* Localized prayer names (VERBATIM PH_PRAYER_NAMES). */
export const PRAYER_NAMES: Record<Locale, Record<string, string>> = {
  en: { fajr: 'Fajr', dhuhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha', sunrise: 'Sunrise' },
  ar: { fajr: 'الفجر', dhuhr: 'الظهر', asr: 'العصر', maghrib: 'المغرب', isha: 'العشاء', sunrise: 'الشروق' },
  ur: { fajr: 'فجر', dhuhr: 'ظہر', asr: 'عصر', maghrib: 'مغرب', isha: 'عشاء', sunrise: 'طلوعِ آفتاب' }
};

/* ──────────────────────────────────────────────────────────────
   Full UI copy dictionary (meaningful, idiomatic en/ar/ur).
   Translations are localized for sense — not word-for-word — using
   the religious register Arabic/Urdu speakers expect for salah copy.
   ────────────────────────────────────────────────────────────── */

export interface UICopy {
  /* nav + chrome */
  navPrayer: string;
  navCalendar: string;
  navConverter: string;
  navEvents: string;
  brandSubtitle: string;
  brandPrayer: string;
  brandCalendar: string;
  themeSystem: string;
  themeLight: string;
  themeDark: string;
  themeAria: string;
  languageAria: string;
  refresh: string;
  refreshing: string;
  today: string;
  current: string;
  currentMonth: string;
  close: string;
  settings: string;
  hide: string;

  /* location / city picker */
  selectCity: string;
  selectCityShort: string;
  selectCityDots: string;
  searchCityAria: string;
  search: string;
  searching: string;
  useMyLocation: string;
  selectMatch: string;
  searchFailed: string;
  couldNotGetLocation: string;
  locationChangeDetected: string;
  showing: string;
  detected: string;
  switchBtn: string;
  keepBtn: string;

  /* date / strip */
  locationLabel: string;
  dateLabel: string;
  monthLabel: string;
  prevDayAria: string;
  nextDayAria: string;
  dateControls: string;
  activeDate: string;
  prev: string;
  next: string;

  /* settings fields */
  timeFormat: string;
  calculation: string;
  calculationMethod: string;
  asrMadhhab: string;
  asrJuristic: string;
  shafi: string;
  hanafi: string;
  autoByCity: string;
  manual: string;
  hijriDateMode: string;
  autoByRegion: string;
  manualOverride: string;
  currentHijriProfile: string;
  regionDefault: string;
  dayWord: string;
  daysWord: string;
  manualHijriAdjustment: string;
  hijriAdjustment: string;
  noteLabel: string;
  settingsIntro: string;
  settingsAutoNote: string;
  openSettingsAria: string;
  decAdjAria: string;
  incAdjAria: string;
  fajrFard: string;
  asrFard: string;
  minAfterAdhan: string;
  sourceAladhan: string;
  timezoneLabel: string;
  updatedLabel: string;

  /* hero / countdown */
  upNext: string;
  timeUntilNext: string;
  nextPrefix: string;
  atWord: string;
  asrPrefix: string;
  methodPrefix: string;
  dayProgress: string;
  prayerPhase: string;
  todayWord: string;
  notAPrayer: string;
  badgeCurrent: string;
  badgeUpNext: string;
  timesMayVary: string;
  hijriDisclaimer: string;

  /* events */
  upcomingEvents: string;
  estimated: string;
  hijriPrefix: string;
  rangePrefix: string;
  eventsStripNote: string;
  islamicEvents: string;
  eventsIntro: string;
  chooseLocation: string;
  chooseLocationBody: string;
  couldntRefreshEvents: string;
  showingCached: string;
  filters: string;
  allHijriMonths: string;
  thEvent: string;
  thCategory: string;
  thHijri: string;
  thEstimatedRange: string;
  thImportance: string;
  noEventsMatch: string;
  selectLocationLoad: string;
  defaultDisclaimer: string;
  moreSuffix: string;
  moreThisMonth: string;
  inDays: string; // "in ~{n} days"

  /* timetable */
  thDate: string;
  fullTimetable: string;
  timetableSub: string;
  tabToday: string;
  tabWeek: string;
  tabMonth: string;
  tabRamadan: string;
  tabCalendar: string;
  dailyTabNote: string;
  thDay: string;
  thSuhoor: string;
  thIftar: string;
  noRamadan: string;
  aboutTheseTimes: string;
  footerAboutBody: string;
  fullMonthTimetable: string;
  allDaysSub: string; // "All {n} days · prayer times"
  couldntRefresh: string;

  /* calendar */
  calendarView: string;
  calendarGridNote: string;
  legendToday: string;
  legendMajor: string;
  legendEid: string;
  todayEyebrow: string;
  selectedEyebrow: string;
  jumuah: string;
  aboutTheseDates: string;
  calFooterBody: string;

  /* converter */
  converterTitle: string;
  converterIntro: string;
  errorWord: string;
  g2hTitle: string;
  g2hSub: string;
  convert: string;
  adjustmentPrefix: string;
  weekdayPrefix: string;
  h2gTitle: string;
  h2gSub: string;
  hijriDayAria: string;
  hijriMonthAria: string;
  hijriYearAria: string;
  h2gNote: string;
  disclaimerLabel: string;
  converterDisclaimer: string;
  convertFailed: string;

  /* footer */
  footerLong: string;
}

export const UI: Record<Locale, UICopy> = {
  en: {
    navPrayer: 'Prayer', navCalendar: 'Calendar', navConverter: 'Converter', navEvents: 'Events',
    brandSubtitle: 'Prayer Times', brandPrayer: 'Prayer', brandCalendar: 'Calendar',
    themeSystem: 'System', themeLight: 'Light', themeDark: 'Dark', themeAria: 'Theme',
    languageAria: 'Language', refresh: 'Refresh', refreshing: 'Refreshing…', today: 'Today',
    current: 'Current', currentMonth: 'Current', close: 'Close', settings: 'Settings', hide: 'Hide',
    selectCity: 'Select a city', selectCityShort: 'Select a city', selectCityDots: 'Select a city…',
    searchCityAria: 'Search city', search: 'Search', searching: 'Searching…',
    useMyLocation: 'Use my location', selectMatch: 'Select a match', searchFailed: 'Search failed',
    couldNotGetLocation: 'Could not get your location', locationChangeDetected: 'Location change detected',
    showing: 'Showing', detected: 'Detected', switchBtn: 'Switch', keepBtn: 'Keep',
    locationLabel: 'Location', dateLabel: 'Date', monthLabel: 'Month',
    prevDayAria: 'Previous day', nextDayAria: 'Next day', dateControls: 'Date controls',
    activeDate: 'Active date', prev: '← Prev', next: 'Next →',
    timeFormat: 'Time format', calculation: 'Calculation', calculationMethod: 'Calculation method',
    asrMadhhab: 'Asr (madhhab)', asrJuristic: 'Asr juristic method (madhhab)',
    shafi: 'Shafi/Other', hanafi: 'Hanafi', autoByCity: 'Auto by city', manual: 'Manual',
    hijriDateMode: 'Hijri date mode', autoByRegion: 'Auto by region', manualOverride: 'Manual override',
    currentHijriProfile: 'Current Hijri profile', regionDefault: 'Region default',
    dayWord: 'day', daysWord: 'days', manualHijriAdjustment: 'Manual Hijri adjustment',
    hijriAdjustment: 'Hijri adjustment', noteLabel: 'Note', settingsIntro: 'These settings affect prayer calculations and date display. Times may vary by method and local mosque practice.',
    settingsAutoNote: 'Auto mode uses a region-aware default so places like Pakistan can follow local moon-sighting practice more closely. For important worship decisions, follow your local masjid or official authority.',
    openSettingsAria: 'Open settings', decAdjAria: 'Decrease hijri adjustment', incAdjAria: 'Increase hijri adjustment',
    fajrFard: 'Fajr fard completion', asrFard: 'Asr fard completion', minAfterAdhan: 'min after adhan',
    sourceAladhan: 'Source: AlAdhan', timezoneLabel: 'Timezone', updatedLabel: 'Updated',
    upNext: 'UP NEXT', timeUntilNext: 'Time until next prayer', nextPrefix: 'Next', atWord: 'at',
    asrPrefix: 'Asr', methodPrefix: 'Method', dayProgress: 'Day progress', prayerPhase: 'Prayer phase',
    todayWord: 'Today', notAPrayer: 'Not a prayer', badgeCurrent: 'Current', badgeUpNext: 'Up next',
    timesMayVary: 'Times may vary based on calculation method and local mosque practice.',
    hijriDisclaimer: 'Hijri dates and event dates can vary by moon sighting; shown as estimated unless you follow a local authority.',
    upcomingEvents: 'Upcoming Islamic events', estimated: 'Estimated', hijriPrefix: 'Hijri', rangePrefix: 'Range',
    eventsStripNote: 'Hijri dates and event dates can vary by moon sighting; treat these as approximate ranges.',
    islamicEvents: 'Islamic events', eventsIntro: 'Dates are shown as estimated ranges because moon sighting and local authority can shift the actual day.',
    chooseLocation: 'Choose a location', chooseLocationBody: 'Select a city to load backend-aligned Islamic event estimates for your region and Hijri profile.',
    couldntRefreshEvents: 'Couldn’t refresh events', showingCached: 'Showing the last cached event results if available.',
    filters: 'Filters', allHijriMonths: 'All Hijri months', thEvent: 'Event', thCategory: 'Category',
    thHijri: 'Hijri', thEstimatedRange: 'Estimated Gregorian range', thImportance: 'Importance',
    noEventsMatch: 'No events match the filter.', selectLocationLoad: 'Select a location to load events.',
    defaultDisclaimer: 'Hijri dates and event dates can vary by moon sighting; shown as estimated unless you follow a local authority.',
    moreSuffix: 'more', moreThisMonth: 'more this month', inDays: 'in ~{n} days',
    thDate: 'Date', fullTimetable: 'Full timetable', timetableSub: 'Week · Month · Ramadan · Calendar',
    tabToday: 'Today', tabWeek: 'Week', tabMonth: 'Month', tabRamadan: 'Ramadan', tabCalendar: 'Calendar',
    dailyTabNote: 'Today’s prayer times are shown in the strip above. Use this tab for additional daily details in future updates.',
    thDay: 'Day', thSuhoor: 'Suhoor', thIftar: 'Iftar',
    noRamadan: 'No Ramadan days in this month. Switch to the month containing Ramadan.',
    aboutTheseTimes: 'About these times',
    footerAboutBody: 'Times may vary based on calculation method and local mosque practice. Hijri dates and event dates can vary by moon sighting; shown as estimated unless you follow a local authority.',
    fullMonthTimetable: 'Full month timetable', allDaysSub: 'All {n} days · prayer times', couldntRefresh: 'Couldn’t refresh',
    calendarView: 'Calendar view',
    calendarGridNote: 'This calendar is aligned to your selected Gregorian month but shows Hijri dates on each day. Event highlights are estimated.',
    legendToday: 'Today', legendMajor: 'Major', legendEid: 'Eid', todayEyebrow: 'TODAY', selectedEyebrow: 'SELECTED',
    jumuah: 'Jumuʿah', aboutTheseDates: 'About these dates',
    calFooterBody: 'Hijri dates and event dates can vary by moon sighting; shown as estimates unless you follow a local authority. The calendar is aligned to your selected Gregorian month — both Gregorian and Hijri numerals are shown on each day.',
    converterTitle: 'Hijri ↔ Gregorian converter',
    converterIntro: 'Hijri dates can vary by moon sighting and local authority. Use the adjustment setting if your community follows a different civil calendar offset.',
    errorWord: 'Error', g2hTitle: 'Gregorian → Hijri', g2hSub: 'Uses the backend converter with your active Hijri adjustment profile.',
    convert: 'Convert', adjustmentPrefix: 'Adjustment', weekdayPrefix: 'Weekday',
    h2gTitle: 'Hijri → Gregorian', h2gSub: 'Uses the backend converter. Hijri → Gregorian follows the upstream civil calendar response.',
    hijriDayAria: 'Hijri day', hijriMonthAria: 'Hijri month', hijriYearAria: 'Hijri year',
    h2gNote: 'Note: actual observance can differ by moon sighting.', disclaimerLabel: 'Disclaimer',
    converterDisclaimer: 'Hijri dates can vary by moon sighting and local authority. The displayed conversion reflects your selected adjustment mode for Gregorian → Hijri.',
    convertFailed: 'Convert failed',
    footerLong: 'Times may vary by calculation method and local mosque practice. Hijri dates and events can vary by moon sighting; shown as estimates unless you follow a local authority.'
  },
  ar: {
    navPrayer: 'الصلاة', navCalendar: 'التقويم', navConverter: 'المحوّل', navEvents: 'المناسبات',
    brandSubtitle: 'مواقيت الصلاة', brandPrayer: 'الصلاة', brandCalendar: 'التقويم',
    themeSystem: 'تلقائي', themeLight: 'فاتح', themeDark: 'داكن', themeAria: 'المظهر',
    languageAria: 'اللغة', refresh: 'تحديث', refreshing: 'جارٍ التحديث…', today: 'اليوم',
    current: 'الحالية', currentMonth: 'هذا الشهر', close: 'إغلاق', settings: 'الإعدادات', hide: 'إخفاء',
    selectCity: 'اختر مدينة', selectCityShort: 'اختر مدينة', selectCityDots: 'اختر مدينة…',
    searchCityAria: 'البحث عن مدينة', search: 'بحث', searching: 'جارٍ البحث…',
    useMyLocation: 'استخدم موقعي', selectMatch: 'اختر النتيجة المطابقة', searchFailed: 'تعذّر البحث',
    couldNotGetLocation: 'تعذّر تحديد موقعك', locationChangeDetected: 'تم رصد تغيّر في الموقع',
    showing: 'المعروض', detected: 'المكتشف', switchBtn: 'تبديل', keepBtn: 'إبقاء',
    locationLabel: 'الموقع', dateLabel: 'التاريخ', monthLabel: 'الشهر',
    prevDayAria: 'اليوم السابق', nextDayAria: 'اليوم التالي', dateControls: 'ضبط التاريخ',
    activeDate: 'التاريخ الحالي', prev: '→ السابق', next: 'التالي ←',
    timeFormat: 'صيغة الوقت', calculation: 'طريقة الحساب', calculationMethod: 'طريقة حساب المواقيت',
    asrMadhhab: 'العصر (المذهب)', asrJuristic: 'مذهب حساب العصر',
    shafi: 'الشافعي/الجمهور', hanafi: 'الحنفي', autoByCity: 'تلقائي حسب المدينة', manual: 'يدوي',
    hijriDateMode: 'وضع التاريخ الهجري', autoByRegion: 'تلقائي حسب المنطقة', manualOverride: 'تعديل يدوي',
    currentHijriProfile: 'الإعداد الهجري الحالي', regionDefault: 'الافتراضي للمنطقة',
    dayWord: 'يوم', daysWord: 'أيام', manualHijriAdjustment: 'تعديل هجري يدوي',
    hijriAdjustment: 'تعديل هجري', noteLabel: 'ملاحظة',
    settingsIntro: 'تؤثّر هذه الإعدادات في حساب المواقيت وعرض التاريخ. وقد تختلف الأوقات باختلاف الطريقة وعادة المسجد المحلي.',
    settingsAutoNote: 'يعتمد الوضع التلقائي على افتراضٍ مراعٍ للمنطقة، فأماكن مثل باكستان تتبع رؤية الهلال المحلية بدقّة أكبر. وللقرارات العبادية المهمة اتبع مسجدك أو الجهة الرسمية المعتمدة.',
    openSettingsAria: 'فتح الإعدادات', decAdjAria: 'إنقاص التعديل الهجري', incAdjAria: 'زيادة التعديل الهجري',
    fajrFard: 'انقضاء فرض الفجر', asrFard: 'انقضاء فرض العصر', minAfterAdhan: 'دقيقة بعد الأذان',
    sourceAladhan: 'المصدر: AlAdhan', timezoneLabel: 'المنطقة الزمنية', updatedLabel: 'آخر تحديث',
    upNext: 'الصلاة التالية', timeUntilNext: 'الوقت حتى الصلاة التالية', nextPrefix: 'التالية', atWord: 'عند',
    asrPrefix: 'العصر', methodPrefix: 'الطريقة', dayProgress: 'تقدّم اليوم', prayerPhase: 'مرحلة الصلاة',
    todayWord: 'اليوم', notAPrayer: 'ليست صلاة', badgeCurrent: 'الحالية', badgeUpNext: 'التالية',
    timesMayVary: 'قد تختلف الأوقات باختلاف طريقة الحساب وعادة المسجد المحلي.',
    hijriDisclaimer: 'قد تختلف التواريخ الهجرية وتواريخ المناسبات حسب رؤية الهلال؛ وتُعرض تقديريًا ما لم تتبع جهة محلية معتمدة.',
    upcomingEvents: 'المناسبات الإسلامية القادمة', estimated: 'تقديري', hijriPrefix: 'هجري', rangePrefix: 'المدى',
    eventsStripNote: 'قد تختلف التواريخ الهجرية وتواريخ المناسبات حسب رؤية الهلال؛ فاعتبرها مدًى تقريبية.',
    islamicEvents: 'المناسبات الإسلامية', eventsIntro: 'تُعرض التواريخ كمدًى تقديرية لأن رؤية الهلال والجهة المحلية قد تغيّران اليوم الفعلي.',
    chooseLocation: 'اختر موقعًا', chooseLocationBody: 'اختر مدينة لتحميل تقديرات المناسبات الإسلامية المتوافقة مع منطقتك وإعدادك الهجري.',
    couldntRefreshEvents: 'تعذّر تحديث المناسبات', showingCached: 'تُعرض آخر نتائج محفوظة إن وُجدت.',
    filters: 'عوامل التصفية', allHijriMonths: 'كل الأشهر الهجرية', thEvent: 'المناسبة', thCategory: 'الفئة',
    thHijri: 'هجري', thEstimatedRange: 'المدى الميلادي التقديري', thImportance: 'الأهمية',
    noEventsMatch: 'لا توجد مناسبات مطابقة للتصفية.', selectLocationLoad: 'اختر موقعًا لتحميل المناسبات.',
    defaultDisclaimer: 'قد تختلف التواريخ الهجرية وتواريخ المناسبات حسب رؤية الهلال؛ وتُعرض تقديريًا ما لم تتبع جهة محلية معتمدة.',
    moreSuffix: 'أخرى', moreThisMonth: 'أخرى هذا الشهر', inDays: 'بعد ~{n} يومًا',
    thDate: 'التاريخ', fullTimetable: 'الجدول الكامل', timetableSub: 'أسبوع · شهر · رمضان · تقويم',
    tabToday: 'اليوم', tabWeek: 'أسبوع', tabMonth: 'شهر', tabRamadan: 'رمضان', tabCalendar: 'تقويم',
    dailyTabNote: 'تظهر مواقيت اليوم في الشريط أعلاه. خصّصنا هذا التبويب لتفاصيل يومية إضافية في تحديثات قادمة.',
    thDay: 'اليوم', thSuhoor: 'السحور', thIftar: 'الإفطار',
    noRamadan: 'لا أيام رمضانية في هذا الشهر. انتقل إلى الشهر الذي يقع فيه رمضان.',
    aboutTheseTimes: 'حول هذه المواقيت',
    footerAboutBody: 'قد تختلف الأوقات باختلاف طريقة الحساب وعادة المسجد المحلي. وقد تختلف التواريخ الهجرية وتواريخ المناسبات حسب رؤية الهلال؛ وتُعرض تقديريًا ما لم تتبع جهة محلية معتمدة.',
    fullMonthTimetable: 'جدول الشهر الكامل', allDaysSub: 'كل الأيام {n} · مواقيت الصلاة', couldntRefresh: 'تعذّر التحديث',
    calendarView: 'عرض التقويم',
    calendarGridNote: 'يتوافق هذا التقويم مع الشهر الميلادي المختار، لكنه يعرض التاريخ الهجري لكل يوم. وإبرازات المناسبات تقديرية.',
    legendToday: 'اليوم', legendMajor: 'كبرى', legendEid: 'عيد', todayEyebrow: 'اليوم', selectedEyebrow: 'المحدد',
    jumuah: 'الجُمُعة', aboutTheseDates: 'حول هذه التواريخ',
    calFooterBody: 'قد تختلف التواريخ الهجرية وتواريخ المناسبات حسب رؤية الهلال؛ وتُعرض تقديريًا ما لم تتبع جهة محلية معتمدة. ويتوافق التقويم مع الشهر الميلادي المختار — ويُعرض الرقمان الميلادي والهجري في كل يوم.',
    converterTitle: 'محوّل هجري ↔ ميلادي',
    converterIntro: 'قد تختلف التواريخ الهجرية حسب رؤية الهلال والجهة المحلية. استخدم إعداد التعديل إذا كان مجتمعك يتبع فرقًا مختلفًا في التقويم المدني.',
    errorWord: 'خطأ', g2hTitle: 'ميلادي → هجري', g2hSub: 'يستخدم المحوّل الخلفي مع إعداد التعديل الهجري الفعّال لديك.',
    convert: 'حوّل', adjustmentPrefix: 'التعديل', weekdayPrefix: 'اليوم',
    h2gTitle: 'هجري → ميلادي', h2gSub: 'يستخدم المحوّل الخلفي. ويتبع التحويل من الهجري إلى الميلادي استجابة التقويم المدني المصدري.',
    hijriDayAria: 'اليوم الهجري', hijriMonthAria: 'الشهر الهجري', hijriYearAria: 'السنة الهجرية',
    h2gNote: 'ملاحظة: قد يختلف الثبوت الفعلي حسب رؤية الهلال.', disclaimerLabel: 'تنبيه',
    converterDisclaimer: 'قد تختلف التواريخ الهجرية حسب رؤية الهلال والجهة المحلية. ويعكس التحويل المعروض وضع التعديل الذي اخترته للتحويل من الميلادي إلى الهجري.',
    convertFailed: 'تعذّر التحويل',
    footerLong: 'قد تختلف الأوقات باختلاف طريقة الحساب وعادة المسجد المحلي. وقد تختلف التواريخ الهجرية والمناسبات حسب رؤية الهلال؛ وتُعرض تقديريًا ما لم تتبع جهة محلية معتمدة.'
  },
  ur: {
    navPrayer: 'نماز', navCalendar: 'کیلنڈر', navConverter: 'کنورٹر', navEvents: 'مواقع',
    brandSubtitle: 'اوقاتِ نماز', brandPrayer: 'نماز', brandCalendar: 'کیلنڈر',
    themeSystem: 'سسٹم', themeLight: 'روشن', themeDark: 'گہرا', themeAria: 'تھیم',
    languageAria: 'زبان', refresh: 'تازہ کریں', refreshing: 'تازہ ہو رہا ہے…', today: 'آج',
    current: 'موجودہ', currentMonth: 'رواں ماہ', close: 'بند کریں', settings: 'ترتیبات', hide: 'چھپائیں',
    selectCity: 'شہر منتخب کریں', selectCityShort: 'شہر منتخب کریں', selectCityDots: 'شہر منتخب کریں…',
    searchCityAria: 'شہر تلاش کریں', search: 'تلاش', searching: 'تلاش جاری…',
    useMyLocation: 'میرا مقام استعمال کریں', selectMatch: 'مطابق نتیجہ منتخب کریں', searchFailed: 'تلاش ناکام',
    couldNotGetLocation: 'آپ کا مقام معلوم نہ ہو سکا', locationChangeDetected: 'مقام کی تبدیلی محسوس ہوئی',
    showing: 'دکھایا جا رہا', detected: 'پتا چلا', switchBtn: 'بدلیں', keepBtn: 'برقرار رکھیں',
    locationLabel: 'مقام', dateLabel: 'تاریخ', monthLabel: 'مہینہ',
    prevDayAria: 'پچھلا دن', nextDayAria: 'اگلا دن', dateControls: 'تاریخ کی ترتیب',
    activeDate: 'فعال تاریخ', prev: '→ پچھلا', next: 'اگلا ←',
    timeFormat: 'وقت کی صورت', calculation: 'حساب کا طریقہ', calculationMethod: 'اوقات کے حساب کا طریقہ',
    asrMadhhab: 'عصر (مسلک)', asrJuristic: 'عصر کے حساب کا مسلک',
    shafi: 'شافعی/دیگر', hanafi: 'حنفی', autoByCity: 'شہر کے مطابق خودکار', manual: 'دستی',
    hijriDateMode: 'ہجری تاریخ کا انداز', autoByRegion: 'علاقے کے مطابق خودکار', manualOverride: 'دستی تبدیلی',
    currentHijriProfile: 'موجودہ ہجری پروفائل', regionDefault: 'علاقے کی پہلے سے طے شدہ',
    dayWord: 'دن', daysWord: 'دن', manualHijriAdjustment: 'دستی ہجری تطبیق',
    hijriAdjustment: 'ہجری تطبیق', noteLabel: 'نوٹ',
    settingsIntro: 'یہ ترتیبات اوقات کے حساب اور تاریخ کی نمائش پر اثر ڈالتی ہیں۔ اوقات طریقہ اور مقامی مسجد کے عمل کے مطابق مختلف ہو سکتے ہیں۔',
    settingsAutoNote: 'خودکار انداز علاقے کے لحاظ سے طے شدہ قدر استعمال کرتا ہے تاکہ پاکستان جیسے مقامات مقامی رؤیتِ ہلال کی زیادہ قریب سے پیروی کر سکیں۔ اہم عبادتی فیصلوں کے لیے اپنی مقامی مسجد یا سرکاری ادارے کی پیروی کریں۔',
    openSettingsAria: 'ترتیبات کھولیں', decAdjAria: 'ہجری تطبیق کم کریں', incAdjAria: 'ہجری تطبیق بڑھائیں',
    fajrFard: 'فجر فرض کی تکمیل', asrFard: 'عصر فرض کی تکمیل', minAfterAdhan: 'منٹ اذان کے بعد',
    sourceAladhan: 'ماخذ: AlAdhan', timezoneLabel: 'ٹائم زون', updatedLabel: 'آخری تازہ کاری',
    upNext: 'اگلی نماز', timeUntilNext: 'اگلی نماز میں باقی وقت', nextPrefix: 'اگلی', atWord: 'بوقت',
    asrPrefix: 'عصر', methodPrefix: 'طریقہ', dayProgress: 'دن کی پیش رفت', prayerPhase: 'نماز کا مرحلہ',
    todayWord: 'آج', notAPrayer: 'نماز نہیں', badgeCurrent: 'موجودہ', badgeUpNext: 'اگلی',
    timesMayVary: 'اوقات حساب کے طریقہ اور مقامی مسجد کے عمل کے مطابق مختلف ہو سکتے ہیں۔',
    hijriDisclaimer: 'ہجری تواریخ اور مواقع کی تواریخ رؤیتِ ہلال سے مختلف ہو سکتی ہیں؛ مقامی معتبر ادارے کی پیروی نہ ہونے پر تخمینی سمجھیں۔',
    upcomingEvents: 'آنے والے اسلامی مواقع', estimated: 'تخمینی', hijriPrefix: 'ہجری', rangePrefix: 'مدت',
    eventsStripNote: 'ہجری تواریخ اور مواقع کی تواریخ رؤیتِ ہلال سے مختلف ہو سکتی ہیں؛ انہیں تقریبی مدت سمجھیں۔',
    islamicEvents: 'اسلامی مواقع', eventsIntro: 'تواریخ تخمینی مدت کے طور پر دکھائی جاتی ہیں کیونکہ رؤیتِ ہلال اور مقامی ادارہ اصل دن کو بدل سکتے ہیں۔',
    chooseLocation: 'مقام منتخب کریں', chooseLocationBody: 'اپنے علاقے اور ہجری پروفائل کے مطابق اسلامی مواقع کے تخمینے لوڈ کرنے کے لیے شہر منتخب کریں۔',
    couldntRefreshEvents: 'مواقع تازہ نہ ہو سکے', showingCached: 'دستیاب ہونے پر آخری محفوظ نتائج دکھائے جا رہے ہیں۔',
    filters: 'فلٹرز', allHijriMonths: 'تمام ہجری مہینے', thEvent: 'موقع', thCategory: 'قسم',
    thHijri: 'ہجری', thEstimatedRange: 'تخمینی عیسوی مدت', thImportance: 'اہمیت',
    noEventsMatch: 'فلٹر سے کوئی موقع مماثل نہیں۔', selectLocationLoad: 'مواقع لوڈ کرنے کے لیے مقام منتخب کریں۔',
    defaultDisclaimer: 'ہجری تواریخ اور مواقع کی تواریخ رؤیتِ ہلال سے مختلف ہو سکتی ہیں؛ مقامی معتبر ادارے کی پیروی نہ ہونے پر تخمینی سمجھیں۔',
    moreSuffix: 'مزید', moreThisMonth: 'مزید اس ماہ', inDays: '~{n} دن میں',
    thDate: 'تاریخ', fullTimetable: 'مکمل ٹائم ٹیبل', timetableSub: 'ہفتہ · ماہ · رمضان · کیلنڈر',
    tabToday: 'آج', tabWeek: 'ہفتہ', tabMonth: 'ماہ', tabRamadan: 'رمضان', tabCalendar: 'کیلنڈر',
    dailyTabNote: 'آج کے اوقات اوپر کی پٹی میں دکھائے گئے ہیں۔ یہ ٹیب آئندہ اپڈیٹس میں مزید روزانہ تفصیلات کے لیے ہے۔',
    thDay: 'دن', thSuhoor: 'سحری', thIftar: 'افطار',
    noRamadan: 'اس ماہ میں رمضان کے دن نہیں۔ اُس ماہ پر جائیں جس میں رمضان ہے۔',
    aboutTheseTimes: 'ان اوقات کے بارے میں',
    footerAboutBody: 'اوقات حساب کے طریقہ اور مقامی مسجد کے عمل کے مطابق مختلف ہو سکتے ہیں۔ ہجری تواریخ اور مواقع کی تواریخ رؤیتِ ہلال سے مختلف ہو سکتی ہیں؛ مقامی معتبر ادارے کی پیروی نہ ہونے پر تخمینی سمجھیں۔',
    fullMonthTimetable: 'پورے ماہ کا ٹائم ٹیبل', allDaysSub: 'تمام {n} دن · اوقاتِ نماز', couldntRefresh: 'تازہ نہ ہو سکا',
    calendarView: 'کیلنڈر منظر',
    calendarGridNote: 'یہ کیلنڈر آپ کے منتخب عیسوی ماہ کے مطابق ہے مگر ہر دن کی ہجری تاریخ دکھاتا ہے۔ مواقع کی نشاندہی تخمینی ہے۔',
    legendToday: 'آج', legendMajor: 'بڑے', legendEid: 'عید', todayEyebrow: 'آج', selectedEyebrow: 'منتخب',
    jumuah: 'جمعہ', aboutTheseDates: 'ان تواریخ کے بارے میں',
    calFooterBody: 'ہجری تواریخ اور مواقع کی تواریخ رؤیتِ ہلال سے مختلف ہو سکتی ہیں؛ مقامی معتبر ادارے کی پیروی نہ ہونے پر تخمینی سمجھیں۔ کیلنڈر آپ کے منتخب عیسوی ماہ کے مطابق ہے — ہر دن پر عیسوی اور ہجری دونوں اعداد دکھائے جاتے ہیں۔',
    converterTitle: 'ہجری ↔ عیسوی کنورٹر',
    converterIntro: 'ہجری تواریخ رؤیتِ ہلال اور مقامی ادارے سے مختلف ہو سکتی ہیں۔ اگر آپ کی جماعت مختلف سرکاری کیلنڈر فرق کی پیروی کرتی ہے تو تطبیق کی ترتیب استعمال کریں۔',
    errorWord: 'خرابی', g2hTitle: 'عیسوی → ہجری', g2hSub: 'آپ کے فعال ہجری تطبیق پروفائل کے ساتھ بیک اینڈ کنورٹر استعمال کرتا ہے۔',
    convert: 'تبدیل کریں', adjustmentPrefix: 'تطبیق', weekdayPrefix: 'دن',
    h2gTitle: 'ہجری → عیسوی', h2gSub: 'بیک اینڈ کنورٹر استعمال کرتا ہے۔ ہجری سے عیسوی تبدیلی مصدری سرکاری کیلنڈر جواب کی پیروی کرتی ہے۔',
    hijriDayAria: 'ہجری دن', hijriMonthAria: 'ہجری ماہ', hijriYearAria: 'ہجری سال',
    h2gNote: 'نوٹ: اصل ثبوت رؤیتِ ہلال سے مختلف ہو سکتا ہے۔', disclaimerLabel: 'تنبیہ',
    converterDisclaimer: 'ہجری تواریخ رؤیتِ ہلال اور مقامی ادارے سے مختلف ہو سکتی ہیں۔ دکھائی گئی تبدیلی عیسوی → ہجری کے لیے آپ کے منتخب تطبیق انداز کی عکاسی کرتی ہے۔',
    convertFailed: 'تبدیلی ناکام',
    footerLong: 'اوقات حساب کے طریقہ اور مقامی مسجد کے عمل کے مطابق مختلف ہو سکتے ہیں۔ ہجری تواریخ اور مواقع رؤیتِ ہلال سے مختلف ہو سکتے ہیں؛ مقامی معتبر ادارے کی پیروی نہ ہونے پر تخمینی سمجھیں۔'
  }
};

/* Localized Hijri month names (index 0 = Muharram). */
export const HIJRI_MONTHS: Record<Locale, string[]> = {
  en: ['Muharram', 'Safar', 'Rabiʿ al-Awwal', 'Rabiʿ al-Thani', 'Jumada al-Ula', 'Jumada al-Akhirah', 'Rajab', 'Shaʿban', 'Ramadan', 'Shawwal', 'Dhu al-Qaʿdah', 'Dhu al-Hijjah'],
  ar: ['محرّم', 'صفر', 'ربيع الأول', 'ربيع الآخر', 'جمادى الأولى', 'جمادى الآخرة', 'رجب', 'شعبان', 'رمضان', 'شوّال', 'ذو القعدة', 'ذو الحجة'],
  ur: ['محرم', 'صفر', 'ربیع الاول', 'ربیع الثانی', 'جمادی الاولیٰ', 'جمادی الثانیہ', 'رجب', 'شعبان', 'رمضان', 'شوال', 'ذوالقعدہ', 'ذوالحجہ']
};

/* Localized short weekday names, Monday-first (matches the calendar grid order). */
export const WEEKDAYS_SHORT: Record<Locale, string[]> = {
  en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  ar: ['الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت', 'الأحد'],
  ur: ['پیر', 'منگل', 'بدھ', 'جمعرات', 'جمعہ', 'ہفتہ', 'اتوار']
};

/* Localized full weekday names, Sunday-first (JS getDay() order). */
export const WEEKDAYS_LONG: Record<Locale, string[]> = {
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  ar: ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
  ur: ['اتوار', 'پیر', 'منگل', 'بدھ', 'جمعرات', 'جمعہ', 'ہفتہ']
};

/* Localized sun-phase labels (keyed by the English enum from prayerPhase.ts). */
export const SUN_LABELS: Record<Locale, Record<string, string>> = {
  en: { Night: 'Night', Dawn: 'Dawn', Day: 'Day', Afternoon: 'Afternoon', Twilight: 'Twilight' },
  ar: { Night: 'ليل', Dawn: 'فجر', Day: 'نهار', Afternoon: 'عصر', Twilight: 'غسق' },
  ur: { Night: 'رات', Dawn: 'فجر', Day: 'دن', Afternoon: 'سہ پہر', Twilight: 'شام' }
};

/* Localized spiritual reminders (keyed by prayer phase). */
export const PHASE_REMINDERS: Record<Locale, Record<string, string>> = {
  en: {
    fajr: 'Quiet dawn. Let the heart wake gently before the day begins.',
    dhuhr: 'A pause in the middle of life. Return to what matters.',
    asr: 'The afternoon passes quickly. Anchor yourself before time slips.',
    maghrib: 'As light fades, let gratitude rise — end the day with presence.',
    isha: 'Night calm. Release the day and leave your worries with Allah.'
  },
  ar: {
    fajr: 'فجرٌ هادئ. دع القلب يستيقظ برِفقٍ قبل أن يبدأ النهار.',
    dhuhr: 'وقفةٌ في وسط الحياة. ارجع إلى ما يهمّ حقًّا.',
    asr: 'يمضي العصر سريعًا. ثبّت قلبك قبل أن ينساب الوقت.',
    maghrib: 'ومع أفول الضوء، فلتعلُ شكرًا — اختم يومك بحضور قلب.',
    isha: 'سكينة الليل. اترك يومك وأودِع همومك عند الله.'
  },
  ur: {
    fajr: 'پُرسکون فجر۔ دن کے آغاز سے پہلے دل کو نرمی سے بیدار ہونے دیں۔',
    dhuhr: 'زندگی کے بیچوں بیچ ایک وقفہ۔ جو اہم ہے اُس کی طرف لوٹیں۔',
    asr: 'سہ پہر تیزی سے گزرتی ہے۔ وقت پھسلنے سے پہلے خود کو جما لیں۔',
    maghrib: 'روشنی ڈھلتے ہی شکر بلند ہو — دن کا اختتام حضورِ دل کے ساتھ کریں۔',
    isha: 'رات کا سکون۔ دن کو رخصت کریں اور اپنے غم اللہ کے سپرد کر دیں۔'
  }
};

/* Localized Islamic event names + categories, keyed by `${hijriMonth}-${hijriDay}`. */
export const EVENT_NAMES: Record<Locale, Record<string, string>> = {
  en: {
    '1-1': 'Islamic New Year (1 Muharram)', '1-10': 'Day of Ashura (10 Muharram)',
    '9-1': 'Start of Ramadan (1 Ramadan)', '9-27': 'Laylat al-Qadr (estimated, last 10 nights)',
    '10-1': 'Eid al-Fitr (1 Shawwal)', '12-9': 'Day of Arafah (9 Dhu al-Hijjah)',
    '12-10': 'Eid al-Adha (10 Dhu al-Hijjah)', '12-11': 'Days of Tashreeq (11–13 Dhu al-Hijjah)'
  },
  ar: {
    '1-1': 'رأس السنة الهجرية (1 محرّم)', '1-10': 'يوم عاشوراء (10 محرّم)',
    '9-1': 'بداية رمضان (1 رمضان)', '9-27': 'ليلة القدر (تقديرًا، العشر الأواخر)',
    '10-1': 'عيد الفطر (1 شوّال)', '12-9': 'يوم عرفة (9 ذو الحجة)',
    '12-10': 'عيد الأضحى (10 ذو الحجة)', '12-11': 'أيام التشريق (11–13 ذو الحجة)'
  },
  ur: {
    '1-1': 'اسلامی نیا سال (1 محرم)', '1-10': 'یومِ عاشورا (10 محرم)',
    '9-1': 'آغازِ رمضان (1 رمضان)', '9-27': 'لیلۃ القدر (تخمینی، آخری دس راتیں)',
    '10-1': 'عید الفطر (1 شوال)', '12-9': 'یومِ عرفہ (9 ذوالحجہ)',
    '12-10': 'عید الاضحیٰ (10 ذوالحجہ)', '12-11': 'ایامِ تشریق (11–13 ذوالحجہ)'
  }
};

export const EVENT_CATEGORIES: Record<Locale, Record<string, string>> = {
  en: { 'Month/Year': 'Month/Year', 'Fasting/History': 'Fasting/History', Ramadan: 'Ramadan', 'Ramadan/Night': 'Ramadan/Night', Eid: 'Eid', 'Hajj/Fasting': 'Hajj/Fasting', Hajj: 'Hajj' },
  ar: { 'Month/Year': 'شهر/سنة', 'Fasting/History': 'صيام/تاريخ', Ramadan: 'رمضان', 'Ramadan/Night': 'رمضان/ليلة', Eid: 'عيد', 'Hajj/Fasting': 'حج/صيام', Hajj: 'حج' },
  ur: { 'Month/Year': 'ماہ/سال', 'Fasting/History': 'روزہ/تاریخ', Ramadan: 'رمضان', 'Ramadan/Night': 'رمضان/شب', Eid: 'عید', 'Hajj/Fasting': 'حج/روزہ', Hajj: 'حج' }
};

/* Localized calculation-method labels keyed by AlAdhan method id. */
export const CALC_METHOD_LABELS: Record<Locale, Record<number, string>> = {
  en: {
    0: 'Shia Ithna-Ashari (Leva Institute, Qum)', 1: 'University of Islamic Sciences, Karachi',
    2: 'Islamic Society of North America (ISNA)', 3: 'Muslim World League (MWL)', 4: 'Umm al-Qura, Makkah',
    5: 'Egyptian General Authority of Survey', 7: 'Institute of Geophysics, University of Tehran',
    8: 'Gulf Region', 9: 'Kuwait', 10: 'Qatar', 11: 'Majlis Ugama Islam Singapura, Singapore',
    12: 'Union des Organisations Islamiques de France', 13: 'Diyanet İşleri Başkanlığı, Turkey',
    14: 'Spiritual Administration of Muslims of Russia'
  },
  ar: {
    0: 'الشيعة الإثنا عشرية (معهد ليفا، قُم)', 1: 'جامعة العلوم الإسلامية، كراتشي',
    2: 'الجمعية الإسلامية لأمريكا الشمالية (ISNA)', 3: 'رابطة العالم الإسلامي', 4: 'أم القرى، مكة المكرمة',
    5: 'الهيئة المصرية العامة للمساحة', 7: 'معهد الجيوفيزياء، جامعة طهران',
    8: 'منطقة الخليج', 9: 'الكويت', 10: 'قطر', 11: 'مجلس أوغاما الإسلامي، سنغافورة',
    12: 'اتحاد المنظمات الإسلامية في فرنسا', 13: 'رئاسة الشؤون الدينية، تركيا',
    14: 'الإدارة الروحية لمسلمي روسيا'
  },
  ur: {
    0: 'شیعہ اثنا عشری (لیوا انسٹیٹیوٹ، قم)', 1: 'یونیورسٹی آف اسلامک سائنسز، کراچی',
    2: 'اسلامک سوسائٹی آف نارتھ امریکہ (ISNA)', 3: 'رابطہ عالمِ اسلامی', 4: 'ام القریٰ، مکہ مکرمہ',
    5: 'مصری جنرل اتھارٹی آف سروے', 7: 'انسٹیٹیوٹ آف جیو فزکس، یونیورسٹی آف تہران',
    8: 'خلیجی خطہ', 9: 'کویت', 10: 'قطر', 11: 'مجلس اوگاما اسلام سنگاپورہ',
    12: 'فرانس کی اسلامی تنظیموں کا اتحاد', 13: 'دیانت امورِ مذہبی، ترکی',
    14: 'روس کے مسلمانوں کا روحانی ادارہ'
  }
};

/* BCP-47 tag for Intl date formatting; force Latin digits for consistency with HH:MM clock. */
export function bcp47(locale: Locale): string {
  return locale === 'en' ? 'en' : `${locale}-u-nu-latn`;
}

/* {n}-style interpolation. */
export function fmt(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ''));
}

export function hijriMonthName(locale: Locale, monthNumber: number): string {
  return HIJRI_MONTHS[locale][monthNumber - 1] ?? HIJRI_MONTHS.en[monthNumber - 1] ?? String(monthNumber);
}

export function hijriLabel(locale: Locale, hijri: { day: number; month: number; year: number } | null | undefined): string {
  if (!hijri) return '—';
  return `${hijri.day} ${hijriMonthName(locale, hijri.month)} ${hijri.year}`;
}

export function eventName(locale: Locale, hijriMonth: number, hijriDay: number, fallback: string): string {
  return EVENT_NAMES[locale][`${hijriMonth}-${hijriDay}`] ?? fallback;
}

export function eventCategory(locale: Locale, category: string): string {
  return EVENT_CATEGORIES[locale][category] ?? category;
}

export function calcMethodLabel(locale: Locale, id: number, fallback: string): string {
  return CALC_METHOD_LABELS[locale][id] ?? fallback;
}

interface LocaleContextValue {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  setLocale: (l: Locale) => void;
  copy: ArcCopy;
  t: UICopy;
  prayerName: (key: string) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

function readLocale(): Locale {
  // 1) Explicit ?lang= query param wins — gives crawlable, shareable localized URLs
  //    (e.g. /prayer?lang=ar) that search/answer engines can index per language.
  try {
    const param = new URLSearchParams(window.location.search).get('lang');
    if (param && (LOCALES as string[]).includes(param)) return param as Locale;
  } catch {
    /* ignore */
  }
  // 2) Otherwise fall back to the persisted choice (shared with the landing origin).
  try {
    const raw = localStorage.getItem(LOCALE_KEY) as Locale | null;
    if (raw && LOCALES.includes(raw)) return raw;
  } catch {
    /* ignore */
  }
  return 'en';
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => readLocale());

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(LOCALE_KEY, l);
    } catch {
      /* ignore */
    }
    try {
      window.dispatchEvent(new Event('ap:languagechange'));
    } catch {
      /* ignore */
    }
    // Keep the URL's ?lang= in sync so the active language is shareable and the
    // page that crawlers land on matches its hreflang alternate. English is the
    // canonical default, so we drop the param for 'en'.
    try {
      const url = new URL(window.location.href);
      if (l === 'en') url.searchParams.delete('lang');
      else url.searchParams.set('lang', l);
      window.history.replaceState({}, '', url);
    } catch {
      /* ignore */
    }
  }, []);

  const dir: 'ltr' | 'rtl' = isRTL(locale) ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.setAttribute('lang', locale);
    document.documentElement.setAttribute('dir', dir);
  }, [locale, dir]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      dir,
      setLocale,
      copy: ARC_COPY[locale],
      t: UI[locale],
      prayerName: (key: string) => PRAYER_NAMES[locale][key] ?? PRAYER_NAMES.en[key] ?? key
    }),
    [locale, dir, setLocale]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    // Defensive fallback so components never crash if rendered outside the provider.
    return {
      locale: 'en',
      dir: 'ltr',
      setLocale: () => {},
      copy: ARC_COPY.en,
      t: UI.en,
      prayerName: (key: string) => PRAYER_NAMES.en[key] ?? key
    };
  }
  return ctx;
}
